import { createContext, useState, useContext, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  // Au chargement, vérifier si un token existe dans localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedPermissions = localStorage.getItem('permissions');

    if (storedToken && storedUser) {
      setToken(storedToken);
      const userData = JSON.parse(storedUser);
      setUser(userData);
      // Charger les permissions depuis le user stocké ou depuis le localStorage dédié
      if (storedPermissions) {
        setPermissions(JSON.parse(storedPermissions));
      } else if (userData.permissions) {
        setPermissions(userData.permissions);
      }
    }

    setLoading(false);
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    const userPermissions = userData?.permissions || [];
    setPermissions(userPermissions);
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('permissions', JSON.stringify(userPermissions));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setPermissions([]);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
  };

  const refreshUser = (updatedUser) => {
    setUser(updatedUser);
    if (updatedUser?.permissions) {
      setPermissions(updatedUser.permissions);
      localStorage.setItem('permissions', JSON.stringify(updatedUser.permissions));
    }
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const hasPermission = useCallback((permissionKey) => {
    return permissions.includes(permissionKey);
  }, [permissions]);

  // Helpers de rôle conservés pour rétrocompatibilité
  const isManager = () => user?.role === 'manager';
  const isPlayer = () => user?.role === 'player';
  const isCoach = () => user?.role === 'coach';
  const isResponsable = () => user?.role === 'responsable';
  const isCEO = () => user?.role === 'ceo';
  const isCOO = () => user?.role === 'coo';
  const isAdminRole = () => user?.role === 'admin';

  // Helpers redirigés vers les permissions
  const canManageTeam = () => hasPermission('events.create') || hasPermission('availability.edit_team');
  const isAdmin = () => hasPermission('admin.access');
  const canManageAllTeams = () => hasPermission('events.view_all') || hasPermission('events.view_own_game');

  const value = {
    user,
    token,
    loading,
    permissions,
    login,
    logout,
    refreshUser,
    hasPermission,
    isManager,
    isPlayer,
    isCoach,
    isResponsable,
    isCEO,
    isCOO,
    isAdminRole,
    canManageTeam,
    isAdmin,
    canManageAllTeams,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
